import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Card, CardContent } from "./card";
import { Button } from "./button";
import { Input } from "./input";
import collectionsService from "../../services/collectionsService";
import { useDashboardUI } from "../../context/GlobalContext";
import { validateUserRole } from "../../helpers/helpers";
import { userRoles } from "../../helpers/constant";

// Local toggle matching the ToggleSwitch pattern used across the admin portal.
const ToggleSwitch = ({ isActive, onToggle, disabled = false }) => (
  <div
    onClick={() => !disabled && onToggle()}
    className={`relative inline-flex items-center h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
      isActive ? "bg-blue-500" : "bg-gray-300"
    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
  >
    <span
      aria-hidden="true"
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        isActive ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </div>
);

export const CreateCollectionModal = ({ open, onClose, onCreate }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Admin-portal collections are org collections by default. Only a superadmin
  // can promote one to an agent collection, which then reveals the org toggle.
  const [isAgentCollection, setIsAgentCollection] = useState(false);
  const [isOrgCollection, setIsOrgCollection] = useState(true);

  const { selectedFacilityFilter, facilityList } = useDashboardUI();
  const isSuperAdmin = validateUserRole(userRoles.SUPERADMIN);
  const targetFacilityNumber = selectedFacilityFilter?.facility_number ?? null;
  const targetFacilityName = facilityList?.find(
    (f) => String(f.facility_number) === String(targetFacilityNumber)
  )?.name;

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setTouched(false);
      setIsLoading(false);
      setIsAgentCollection(false);
      setIsOrgCollection(true);
    }
  }, [open]);

  const handleToggleAgent = () => {
    setIsAgentCollection((prev) => {
      const next = !prev;
      // Turning agent off returns to the default org-collection behaviour.
      if (!next) setIsOrgCollection(true);
      return next;
    });
  };

  const handleCreate = async () => {
    setTouched(true);
    if (name.trim() && description.trim()) {
      setIsLoading(true);
      try {
        const newCollection = await collectionsService.createCollection(
          name.trim(),
          description.trim(),
          targetFacilityNumber,
          isOrgCollection,
          isAgentCollection
        );
        toast.success("Collection created successfully!");
        onCreate(newCollection);
        onClose();
      } catch (error) {
        console.error("Error creating collection:", error);
        toast.error("Failed to create collection");
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="min-w-[min(350px,90vw)] max-w-[90vw]">
        <Card className="shadow-lg bg-white">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-1">
              Create New Collection
            </h3>
            {isSuperAdmin && targetFacilityName && (
              <p className="text-xs text-gray-500 mb-4">
                Will be created for facility:{" "}
                <span className="font-medium text-gray-700">
                  {targetFacilityName}
                </span>
              </p>
            )}
            {(!isSuperAdmin || !targetFacilityName) && <div className="mb-4" />}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Collection Name
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter collection name"
                className="w-full"
                autoFocus
              />
              {touched && !name.trim() && (
                <span className="text-xs text-red-500">Name is required.</span>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                className="w-full"
              />
              {touched && !description.trim() && (
                <span className="text-xs text-red-500">
                  Description is required.
                </span>
              )}
            </div>
            {isSuperAdmin && (
              <div className="mb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium">
                      Is agent collection
                    </label>
                    <p className="text-xs text-gray-500">
                      Use this collection as an agent knowledge base.
                    </p>
                  </div>
                  <ToggleSwitch
                    isActive={isAgentCollection}
                    onToggle={handleToggleAgent}
                    disabled={isLoading}
                  />
                </div>
                {isAgentCollection && (
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <label className="block text-sm font-medium">
                        Is org collection
                      </label>
                      <p className="text-xs text-gray-500">
                        Also expose it as an organization collection.
                      </p>
                    </div>
                    <ToggleSwitch
                      isActive={isOrgCollection}
                      onToggle={() => setIsOrgCollection((prev) => !prev)}
                      disabled={isLoading}
                    />
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={onClose}
                type="button"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                className="bg-blue-500 text-white"
                onClick={handleCreate}
                disabled={!name.trim() || !description.trim() || isLoading}
                type="button"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </div>
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
